/**
 * Header Component — Case Associates LP Override
 *
 * Overrides the base landing-page Header to style the "Visit Website" CTA
 * button with bg-primary and a white border, matching the CTASection
 * "Contact Us" button styling.
 */

import { useEffect, useRef, useState } from 'react';
// @ts-ignore - vendored package has no type declarations
import {
  useProperty,
  useContentToken,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
// @ts-ignore - vendored package has no type declarations
import type {
  SlotGroupDefinition,
  ComponentContentSchema,
  ComponentDesignSchema,
} from '@vibesites/runtime';
// @ts-ignore - vendored package has no type declarations
import { defineComponentRequirements, branding, contact, urls } from '@vibesites/tokens';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import { MenuIcon, CloseIcon } from '@vibesites/assets';

function ensureAbsoluteUrl(url: string | undefined | null): string {
  if (!url || url === '#') return url || '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function buildCtaUrl(baseUrl: string | null, queryParams: string | null): string {
  const url = ensureAbsoluteUrl(baseUrl);
  if (!queryParams || queryParams.trim() === '' || queryParams.trim() === '?utm_source=') {
    return url;
  }
  const params = queryParams.trim().startsWith('?') ? queryParams.trim() : `?${queryParams.trim()}`;
  if (url.includes('?')) {
    return `${url}&${params.slice(1)}`;
  }
  return `${url}${params}`;
}

const logoSlotDefinition: SlotGroupDefinition = {
  label: 'Header Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_primary_logo',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
    },
  },
};

const ctaLinkSlotDefinition: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    label: {
      label: 'CTA Text',
      type: 'text',
      defaultValue: 'Visit Website',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    query_params: {
      label: 'URL Query Parameters',
      type: 'text',
      description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
      defaultValue: '?utm_source=',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const navItems = [
  { label: 'Overview', href: '#overview' },
  { label: 'Floor Plans', href: '#floorplans' },
  { label: 'Amenities', href: '#amenities' },
  { label: 'Neighborhood', href: '#neighborhood' },
];

export function Header() {
  const property = useProperty();
  const slotGroupOverrides = useSlotGroupOverrides();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const headerRef = useRef<HTMLElement>(null);

  const phone = useContentToken('header.phone');
  const logo = useSlotGroup('header.logo', logoSlotDefinition);
  const ctaLink = useSlotGroup('header.cta_link', ctaLinkSlotDefinition);
  const hasPhone = Boolean(phone && phone.trim());

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight, { passive: true });
    return () => {
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;

        if (delta > 5 && currentY > 100) {
          setIsHeaderHidden(true);
          setMobileMenuOpen(false);
        } else if (delta < -5) {
          setIsHeaderHidden(false);
        }

        lastScrollY.current = currentY;
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--header-visible-height',
      isHeaderHidden ? '0px' : 'var(--header-height, 64px)'
    );
  }, [isHeaderHidden]);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-[1000] bg-secondary shadow-md transition-transform duration-300 ease-in-out font-body ${
          isHeaderHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
        style={{ top: 'var(--offer-banner-height, 0px)' }}
      >
        {/* ── Mobile bar: logo + hamburger (< 640px) ── */}
        <div className="flex items-center justify-between px-4 py-3 sm:hidden">
          <EditableSlotGroup
            groupPath="header.logo"
            definition={logoSlotDefinition}
            override={slotGroupOverrides['header.logo']}
            style={{ flexShrink: 0 }}
          >
            <a href="/" className="block no-underline">
              {logo.image ? (
                <VibesitesImage
                  url={logo.image}
                  alt={logo.alt || property.property_name || 'Logo'}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <span className="font-heading text-2xl font-semibold text-secondary-foreground">
                  {property.property_name || 'Property'}
                </span>
              )}
            </a>
          </EditableSlotGroup>

          <button
            className="flex border-none bg-transparent p-2 text-secondary-foreground cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="transition-transform duration-200">
              {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
            </span>
          </button>
        </div>

        {/* ── Mobile dropdown menu ── */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out sm:hidden ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1 bg-secondary px-6 py-4 shadow-lg">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="border-b border-secondary-foreground/[0.08] py-3 font-body text-base font-medium text-secondary-foreground no-underline transition-colors duration-200 hover:opacity-80"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}

            <div className="my-2 h-px bg-secondary-foreground/15" />

            {hasPhone && (
              <a
                href={`tel:${phone}`}
                className="font-accent py-3 text-lg font-semibold text-secondary-foreground no-underline"
              >
                {phone}
              </a>
            )}

            <a
              href={buildCtaUrl(ctaLink.url, ctaLink.query_params)}
              target={ctaLink._target || '_blank'}
              rel="noopener noreferrer"
              className="mt-2 block rounded-md bg-primary px-6 py-3.5 text-center font-body text-base font-semibold text-white no-underline border-2 border-white transition-all duration-200 hover:shadow-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              {ctaLink.label || 'Visit Website'}
            </a>
          </div>
        </div>

        {/* ── Desktop / tablet bar: 3-column grid (≥ 640px) ── */}
        <div className="mx-auto hidden max-w-[1400px] sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-6 sm:px-6 sm:py-4 lg:py-5">
          <div className="flex w-full items-center justify-start">
            <EditableSlotGroup
              groupPath="header.logo"
              definition={logoSlotDefinition}
              override={slotGroupOverrides['header.logo']}
              style={{ flexShrink: 0 }}
            >
              <a href="/" className="block no-underline">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || property.property_name || 'Logo'}
                    className="h-10 w-auto object-contain md:h-[50px] lg:h-[60px]"
                  />
                ) : (
                  <span className="font-heading text-2xl font-semibold text-secondary-foreground">
                    {property.property_name || 'Property'}
                  </span>
                )}
              </a>
            </EditableSlotGroup>
          </div>

          <EditableToken
            path="header.phone"
            type="phone"
            label="Phone Number"
            as="div"
            className="flex items-center justify-center"
          >
            {hasPhone ? (
              <a
                href={`tel:${phone}`}
                className="font-accent text-center text-lg font-bold text-secondary-foreground no-underline transition-opacity duration-200 hover:opacity-80 md:text-xl lg:text-2xl"
              >
                {phone}
              </a>
            ) : (
              <span className="font-accent text-center text-lg font-bold text-secondary-foreground md:text-xl lg:text-2xl" />
            )}
          </EditableToken>

          <div className="flex w-full items-center justify-end">
            <EditableSlotGroup
              groupPath="header.cta_link"
              definition={ctaLinkSlotDefinition}
              override={slotGroupOverrides['header.cta_link']}
              style={{ display: 'inline-block' }}
            >
              <a
                href={buildCtaUrl(ctaLink.url, ctaLink.query_params)}
                target={ctaLink._target || '_blank'}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-body text-sm font-semibold text-white no-underline border-2 border-white transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]"
              >
                {ctaLink.label || 'Visit Website'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </header>

      {/* Spacer to prevent content from hiding behind fixed header + banner */}
      <div style={{ height: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }} />
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '555-555-5555',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Header Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          defaultField: 'property_primary_logo',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'property_name',
        },
      },
    },
    'header.cta_link': {
      label: 'CTA Link',
      fields: {
        label: {
          label: 'CTA Text',
          type: 'text',
          defaultValue: 'Visit Website',
        },
        url: {
          label: 'CTA URL',
          type: 'url',
          defaultField: 'property_website_url',
          suggestedFields: ['property_website_url', 'property_apply_now_url'],
        },
        query_params: {
          label: 'URL Query Parameters',
          type: 'text',
          description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
          defaultValue: '?utm_source=',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_blank',
        },
      },
    },
  },
};

export const designSchema: ComponentDesignSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'color.primary': {
      label: 'Background Color',
      type: 'color',
      description: 'Background color used by the fixed header.',
      cssVariable: '--vibe-color-primary',
    },
    'color.secondary': {
      label: 'CTA Background Color',
      type: 'color',
      description: 'Background color used by the CTA button.',
      cssVariable: '--vibe-color-secondary',
    },
    'color.textOnPrimary': {
      label: 'Header Text Color',
      type: 'color',
      description: 'Text color used on the primary header background.',
      cssVariable: '--vibe-color-text-on-primary',
    },
    'color.textOnSecondary': {
      label: 'CTA Text Color',
      type: 'color',
      description: 'Text color used on the CTA button.',
      cssVariable: '--vibe-color-text-on-secondary',
    },
    'font.heading': {
      label: 'Logo Font',
      type: 'font',
      description: 'Font family used when the logo falls back to property name text.',
      cssVariable: '--vibe-font-heading',
    },
    'font.body': {
      label: 'CTA Font',
      type: 'font',
      description: 'Font family used by the CTA button.',
      cssVariable: '--vibe-font-body',
    },
    'font.accent': {
      label: 'Phone Font',
      type: 'font',
      description: 'Font family used for the prominent phone number.',
      cssVariable: '--vibe-font-accent',
    },
    'shadow.md': {
      label: 'Header Shadow',
      type: 'shadow',
      description: 'Shadow applied below the fixed header.',
      cssVariable: '--vibe-shadow-md',
    },
    'radius.md': {
      label: 'CTA Radius',
      type: 'radius',
      description: 'Corner radius used by the CTA button.',
      cssVariable: '--vibe-radius-md',
    },
  },
};

export const requirements = defineComponentRequirements('Header', {
  designTokens: [
    'color.primary',
    'color.secondary',
    'color.textOnPrimary',
    'color.textOnSecondary',
    'font.heading',
    'font.body',
    'font.accent',
    'shadow.md',
    'radius.md',
  ],
  dataTokens: [
    branding.required('property_name', 'Property name displayed in header'),
    branding.optional('property_primary_logo', 'Logo image'),
    contact.optional('property_phone', 'Phone number'),
    urls.optional('property_website_url', 'Website URL for CTA button'),
  ],
});

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;
(Header as unknown as { designSchema: ComponentDesignSchema }).designSchema = designSchema;
(Header as unknown as { requirements: typeof requirements }).requirements = requirements;

export default Header;
